import { useState } from "react";
import { Link } from "react-router-dom";
import type { GapDetail } from "../types/gap";
import { useShortlist } from "../lib/useShortlist";
import { useVolunteers } from "../lib/useVolunteers";
import { roleLabel } from "../lib/role-label";
import DraftPanel from "./DraftPanel";
import TextDraftPanel from "./TextDraftPanel";

interface Props {
  gap: GapDetail;
  forceVisible?: boolean;
}

interface EmailTarget {
  recipientId: string;
  firstName: string;
}

interface TextTarget {
  recipientId: string;
  firstName: string;
  phone: string | null;
}

// Lean list — wrapper (VolunteersButton) provides the section heading.
// Filters: shortlisted, qualified for the position, not the requester.
// Phone is no longer required — email is the primary contact path.
export default function ShortlistPanel({ gap, forceVisible = false }: Props) {
  const { ids } = useShortlist();
  const { data: volunteers, loading } = useVolunteers();
  const [emailTarget, setEmailTarget] = useState<EmailTarget | null>(null);
  const [textTarget, setTextTarget] = useState<TextTarget | null>(null);

  const naturallyVisible = gap.daysUntilMass <= 2 && !gap.status.resolved;
  if (!naturallyVisible && !forceVisible) return null;

  const filtered = volunteers
    .filter((v) => ids.includes(v.id))
    .filter((v) => v.ministries.includes(gap.ministry))
    .filter((v) => v.id !== gap.requester.volunteerId);

  return (
    <div className="volunteers-list">
      {loading ? (
        <p className="placeholder">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="volunteers-list__empty">
          No shortlisted volunteers qualify for {roleLabel(gap.ministry)}.{" "}
          <Link to="/shortlist">Manage volunteer shortlist&nbsp;→</Link>
        </p>
      ) : (
        <ul className="shortlist__list">
          {filtered.map((v) => (
            <li key={v.id} className="shortlist__row">
              <div className="shortlist__row-text">
                <div className="shortlist__name">
                  {v.firstName} {v.lastName}
                </div>
                <div className="shortlist__contact">
                  {[v.email, v.phone].filter(Boolean).join(" · ") || "no contact on file"}
                </div>
              </div>
              <div className="shortlist__row-actions">
                <button
                  type="button"
                  className="btn"
                  disabled={!v.email}
                  onClick={() =>
                    setEmailTarget({ recipientId: v.id, firstName: v.firstName })
                  }
                >
                  Draft email
                </button>
                {v.phone ? (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() =>
                      setTextTarget({
                        recipientId: v.id,
                        firstName: v.firstName,
                        phone: v.phone,
                      })
                    }
                  >
                    Text
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {filtered.length > 0 ? (
        <p className="volunteers-list__manage">
          <Link to="/shortlist">Manage volunteer shortlist&nbsp;→</Link>
        </p>
      ) : null}

      {emailTarget ? (
        <DraftPanel
          open
          onClose={() => setEmailTarget(null)}
          gapId={gap.id}
          recipient="shortlist"
          recipientId={emailTarget.recipientId}
        />
      ) : null}

      {textTarget ? (
        <TextDraftPanel
          open
          onClose={() => setTextTarget(null)}
          gapId={gap.id}
          recipientId={textTarget.recipientId}
          recipientFirstName={textTarget.firstName}
          recipientPhone={textTarget.phone}
        />
      ) : null}
    </div>
  );
}
